import { ClipboardList, DoorOpen, Link2, Plus, Ticket } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import type { TicketTypeInput } from '~/entities/ticket/api/tickets-api'
import { useTicketSettings } from '~/entities/ticket/model/use-tickets'
import {
  draftTicketType,
  type EventEntry,
  hasPaidTicketTypes,
  ticketTypesPrice,
} from '~/features/events/lib/event-entry'
import { TicketTypeDialog } from '~/features/events/tickets/ticket-type-dialog'
import { TicketTypeList } from '~/features/events/tickets/ticket-type-list'
import * as m from '~/paraglide/messages'
import { cn } from '~/shared/lib/utils'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { CapacityField, PriceField } from './event-pricing-section'
import { TicketLinkField } from './ticket-link-field'

// biome-ignore lint/suspicious/noExplicitAny: TanStack Form API is dynamically typed
type AnyForm = any

const OPTIONS: { value: EventEntry; icon: ReactNode; title: () => string; hint: () => string }[] = [
  { value: 'free', icon: <DoorOpen />, title: m.entryFree, hint: m.entryFreeHint },
  { value: 'tickets', icon: <Ticket />, title: m.entryTickets, hint: m.entryTicketsHint },
  { value: 'signup', icon: <ClipboardList />, title: m.entrySignup, hint: m.entrySignupHint },
  { value: 'link', icon: <Link2 />, title: m.entryLink, hint: m.entryLinkHint },
]

export function EventEntrySection({ form, businessId }: { form: AnyForm; businessId: string }) {
  const choose = (entry: EventEntry) => {
    form.setFieldValue('entry', entry)
    if (entry === 'free') form.setFieldValue('price', { kind: 'free' })
    if (entry === 'tickets') {
      form.setFieldValue('price', ticketTypesPrice(form.getFieldValue('ticketTypes') ?? []))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{m.entryTitle()}</CardTitle>
        <CardDescription>{m.entryDescription()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form.Subscribe
          selector={(state: { values: { entry: EventEntry | null } }) => state.values.entry}
        >
          {(entry: EventEntry | null) => (
            <>
              <fieldset className="grid gap-2 sm:grid-cols-2">
                <legend className="sr-only">{m.entryDescription()}</legend>
                {OPTIONS.map((option) => {
                  const selected = option.value === entry
                  return (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => choose(option.value)}
                      className={cn(
                        'flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left outline-none transition-colors',
                        'focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                        selected
                          ? 'border-brand bg-brand-subtle text-brand-subtle-foreground'
                          : 'border-border hover:border-foreground/25',
                      )}
                    >
                      <span aria-hidden className="mt-0.5 shrink-0 [&_svg]:size-4">
                        {option.icon}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-medium text-sm">{option.title()}</span>
                        <span
                          className={cn(
                            'block text-xs',
                            selected ? 'text-brand-subtle-foreground/80' : 'text-muted-foreground',
                          )}
                        >
                          {option.hint()}
                        </span>
                      </span>
                    </button>
                  )
                })}
              </fieldset>

              {entry === 'free' ? (
                <p className="text-muted-foreground text-sm">{m.entryFreeNote()}</p>
              ) : null}
              {entry === 'tickets' ? <TicketDrafts form={form} businessId={businessId} /> : null}
              {entry === 'signup' ? (
                <p className="text-muted-foreground text-sm">{m.signupCreateHint()}</p>
              ) : null}
              {entry === 'link' ? <TicketLinkField form={form} required /> : null}
              {entry === null || entry === 'signup' || entry === 'link' ? (
                <PriceField form={form} />
              ) : null}
              {entry === 'tickets' ? null : <CapacityField form={form} />}
            </>
          )}
        </form.Subscribe>
      </CardContent>
    </Card>
  )
}

function TicketDrafts({ form, businessId }: { form: AnyForm; businessId: string }) {
  const settings = useTicketSettings(businessId)
  const [dialog, setDialog] = useState<{ open: boolean; index: number | null }>({
    open: false,
    index: null,
  })

  return (
    <form.Field
      name="ticketTypes"
      validators={{
        onSubmit: ({ value }: { value: TicketTypeInput[] }) =>
          value.length === 0 ? m.entryTicketsMissing() : undefined,
      }}
    >
      {/* biome-ignore lint/suspicious/noExplicitAny: TanStack Form field API */}
      {(field: any) => {
        const drafts: TicketTypeInput[] = field.state.value ?? []
        const types = drafts.map(draftTicketType)
        const update = (next: TicketTypeInput[]) => {
          field.handleChange(next)
          form.setFieldValue('price', ticketTypesPrice(next))
        }
        const editing = dialog.index === null ? null : (types[dialog.index] ?? null)
        const needsPayouts =
          hasPaidTicketTypes(drafts) && settings.data && !settings.data.connect.onboardingComplete

        return (
          <div data-field="ticketTypes" className="space-y-3">
            {types.length > 0 ? (
              <TicketTypeList
                types={types}
                confirmDelete={false}
                onEdit={(type) => setDialog({ open: true, index: type.sort })}
                onDelete={(type) => update(drafts.filter((_, index) => index !== type.sort))}
              />
            ) : (
              <p className="text-muted-foreground text-sm">{m.entryTicketsEmpty()}</p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!settings.data}
              onClick={() => setDialog({ open: true, index: null })}
            >
              <Plus />
              {m.ticketsAddType()}
            </Button>
            {field.state.meta.errors?.[0] ? (
              <p className="text-destructive text-sm">{field.state.meta.errors[0]}</p>
            ) : null}
            <p className="text-muted-foreground text-sm">
              {needsPayouts
                ? `${settings.data?.connect.available ? m.ticketsPaidNeedsPayouts() : m.ticketsPaidSoon()} `
                : ''}
              {m.entryTicketsLater()}
            </p>
            {settings.data ? (
              <TicketTypeDialog
                draft
                open={dialog.open}
                onOpenChange={(open) => setDialog((current) => ({ ...current, open }))}
                type={editing}
                settings={settings.data}
                onSave={async (input) => {
                  const index = dialog.index
                  update(
                    index === null
                      ? [...drafts, input]
                      : drafts.map((draft, at) => (at === index ? input : draft)),
                  )
                }}
              />
            ) : null}
          </div>
        )
      }}
    </form.Field>
  )
}
